import { HTTP_REQUEST } from '@/lib/Constants';
import { Endpoints } from '@/lib/Endpoints';
import { fetcher } from '@/lib/Requester';
import { PriceData, PriceDataContainer } from '@/models/Product';

const TTL_MS = 30_000;

const cache = new Map<string, { data: PriceData | null; at: number }>();
const inflight = new Map<string, Promise<PriceData | null>>();

export function collectProductIds(
  items?: Array<{ id?: number | string } | null | undefined> | null,
): number[] {
  const ids: number[] = [];
  for (const item of items || []) {
    const n = Number(item?.id);
    if (Number.isInteger(n) && n > 0) {
      ids.push(n);
    }
  }
  return [...new Set(ids)];
}

export function productIdsFromHomepageItems(
  items?: Array<{
    products?: Array<{ id?: number | string }>;
    product?: { id?: number | string };
  }> | null,
): number[] {
  const ids: number[] = [];
  for (const item of items || []) {
    const n = Number(item?.product?.id);
    if (Number.isInteger(n) && n > 0) {
      ids.push(n);
    }
    for (const product of item?.products || []) {
      const pid = Number(product?.id);
      if (Number.isInteger(pid) && pid > 0) {
        ids.push(pid);
      }
    }
  }
  return [...new Set(ids)];
}

/** Dedupes concurrent /product/prices calls. Pass visible product ids when known. */
export function fetchProductPrices(productIds?: number[]): Promise<PriceData | null> {
  const ids = collectProductIds((productIds || []).map((id) => ({ id })));
  const key = ids.length > 0 ? ids.slice().sort((a, b) => a - b).join(',') : 'all';
  const hit = cache.get(key);
  if (hit && Date.now() - hit.at < TTL_MS) {
    return Promise.resolve(hit.data);
  }

  const pending = inflight.get(key);
  if (pending) return pending;

  const endpoint = ids.length > 0
    ? `${Endpoints.PRICES}?${ids.map((id) => `productIds[]=${id}`).join('&')}`
    : Endpoints.PRICES;

  const req = (async () => {
    try {
      const response = await fetcher<PriceDataContainer>({
        params: {},
        cacheKey: `product_prices_${key}`,
        endpoint,
        method: HTTP_REQUEST.GET,
        cacheEnabled: false,
      });
      const data = response?.items ?? null;
      cache.set(key, { data, at: Date.now() });
      return data;
    } catch {
      return null;
    } finally {
      inflight.delete(key);
    }
  })();

  inflight.set(key, req);
  return req;
}
